import { useEffect, useState } from 'react';

/**
 * Icon data from the API — includes URL and whether it needs backend proxying.
 * CoinGecko URLs work directly. Binance CDN URLs need proxying.
 */
interface IconEntry {
  url: string;
  needsProxy: boolean;
}

/**
 * Module-level cache — persists across component re-renders and re-mounts.
 * Only fetched once per app session, then reused everywhere.
 * IMPORTANT: An empty {} is NOT treated as a valid cache — forces re-fetch.
 */
let iconMapCache: Record<string, IconEntry> | null = null;
let fetchPromise: Promise<Record<string, IconEntry>> | null = null;

/** True if the cache actually has icons (not empty). */
function hasCachedIcons(): boolean {
  return !!iconMapCache && Object.keys(iconMapCache).length > 0;
}

/**
 * No-op — retained for backwards compatibility with any callers.
 * The DB-backed /api/market/coin-logos endpoint covers 7500+ coins,
 * so on-demand per-symbol fetching is no longer necessary.
 */
export function fetchMissing(_rawSymbol: string): void {
  // intentional no-op
}

const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:8000';

// Stock/ETF symbols supported by Ostium or HL xyz DEX.
// Logos are served by Parqet's public CDN (ticker-symbol based, no API key).
// Any symbol in this set gets a Parqet logo URL seeded into the icon cache;
// entries from the CoinGecko-backed DB will override if present.
const STOCK_SYMBOLS = new Set([
  "AAPL","AMD","AMZN","ARM","ASML","AVGO","BABA","BMNR",
  "CAT","COIN","COST","CRCL","CVX","GEV","GLXY","GOOG",
  "HOOD","INTC","LLY","META","MSFT","MSTR","MU","NFLX",
  "NVDA","ORCL","PLTR","RIVN","SBET","SHEL","SMCI","TSLA",
  "TSM","XOM","URA","UNG","XLE",
])

function parqetLogoUrl(symbol: string): string {
  return `https://assets.parqet.com/logos/symbol/${symbol}?format=png`
}

/**
 * Get the final usable URL for an icon entry.
 * Proxies Binance CDN URLs through the backend, leaves CoinGecko URLs as-is.
 */
function resolveIconUrl(entry: IconEntry): string {
  if (!entry.url) return '';
  if (!entry.needsProxy) return entry.url; // CoinGecko — direct access
  // Binance CDN — proxy through backend
  return `${BACKEND_URL}/api/market/proxy-image?url=${encodeURIComponent(entry.url)}`;
}

/**
 * Fetch the token icon map from the API (singleton — only one request in flight).
 */
async function fetchIconMap(): Promise<Record<string, IconEntry>> {
  // Seed with Parqet stock logos — DB entries will override if present
  const normalized: Record<string, IconEntry> = {};
  for (const sym of STOCK_SYMBOLS) {
    normalized[sym] = { url: parqetLogoUrl(sym), needsProxy: false };
  }

  try {
    const res = await fetch('/api/token-icons');
    if (res.ok) {
      const data = await res.json();
      const icons = data.icons || {};
      for (const [symbol, value] of Object.entries(icons)) {
        if (typeof value === 'string') {
          normalized[symbol] = { url: value, needsProxy: true };
        } else {
          normalized[symbol] = value as IconEntry;
        }
      }
    }
  } catch { /* ignore — Parqet logos still loaded */ }

  return normalized;
}

/**
 * Get the icon map, using module-level cache or fetching once.
 * Does NOT cache empty results — will re-fetch until icons are available.
 */
async function getIconMap(): Promise<Record<string, IconEntry>> {
  if (hasCachedIcons()) return iconMapCache!;
  if (fetchPromise) return fetchPromise;

  fetchPromise = fetchIconMap().then((icons) => {
    // Only cache if we got actual icons
    if (Object.keys(icons).length > 0) {
      iconMapCache = icons;
    }
    fetchPromise = null;
    return icons;
  });

  return fetchPromise;
}

/**
 * Get a token icon URL synchronously from the cache (resolved/proxied as needed).
 */
export function getTokenIconSync(symbol: string): string | null {
  if (!iconMapCache) return null;
  const baseSymbol = symbol.replace(/USDT$/i, '').toUpperCase();
  const entry = iconMapCache[baseSymbol];
  return entry ? resolveIconUrl(entry) : null;
}

/**
 * Hook that provides token icon lookup.
 * Fetches the icon map once on first use, then returns from cache.
 * Automatically resolves URLs — proxying Binance CDN, direct for CoinGecko.
 */
export function useTokenIcons() {
  const [icons, setIcons] = useState<Record<string, IconEntry>>(iconMapCache || {});
  const [isLoaded, setIsLoaded] = useState(hasCachedIcons());

  useEffect(() => {
    if (hasCachedIcons()) {
      setIcons(iconMapCache!);
      setIsLoaded(true);
      return;
    }

    getIconMap().then((map) => {
      setIcons(map);
      setIsLoaded(true);
    });
  }, []);

  /**
   * Get the resolved icon URL for a token symbol.
   * Accepts both "BTC" and "BTCUSDT" formats.
   * Reads from module-level cache so it reflects on-demand fetches immediately.
   * Returns ready-to-use URL (proxied if needed) or null.
   */
  const getIcon = (symbol: string): string | null => {
    const baseSymbol = symbol.replace(/USDT$/i, '').toUpperCase();
    // Prefer module-level cache over stale state snapshot
    const map = iconMapCache ?? icons;
    const entry = map[baseSymbol];
    return entry ? resolveIconUrl(entry) : null;
  };

  return { getIcon, isLoaded, icons };
}
